import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Send, Save, Trash2, Check, Percent, Download, ImageDown, Film } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DateField } from "@/components/DateField";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const local = (u) => u.replace(/^https?:\/\/[^/]+/, BACKEND);

function DesignPicker() {
  const [d, setD] = useState(null);
  const [sel, setSel] = useState([]);
  const [busy, setBusy] = useState(false);
  const [making, setMaking] = useState(null);
  useEffect(() => { api.get("/admin/instagram/discount-designs").then((r) => { setD(r.data); setSel(r.data.selected); }); }, []);
  if (!d) return <Loader2 className="w-5 h-5 animate-spin text-primary" />;
  const toggle = (n) => setSel((s) => (s.includes(n) ? s.filter((x) => x !== n) : [...s, n]));
  const save = async () => {
    setBusy(true);
    try { const { data } = await api.put("/admin/instagram/discount-designs", { selected: sel }); setSel(data.selected); toast.success("Diseños guardados"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-3" data-testid="ig-discount-designs">
      <p className="text-sm text-muted-foreground">Marca con el círculo los diseños que quieres usar (puedes activar los 6). Cada vez que crees un período de descuento se prepara una publicación con uno de los diseños activos al azar, nunca igual al anterior, con 3 fotos de la comunidad. Toca un diseño para crear la imagen o el reel y descargarlo para subirlo a mano.</p>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {d.designs.map(({ n, name }) => {
          const on = sel.includes(n);
          return (
            <div key={n} className={`relative rounded-xl overflow-hidden border-2 text-left transition-[border-color,box-shadow] ${on ? "border-primary shadow-lg" : "border-transparent hover:border-primary/40"}`} data-testid={`ig-design-${n}`}>
              <button type="button" onClick={() => setMaking({ n, name })} className="block w-full group" aria-label={`Crear y descargar diseño ${n}`} data-testid={`ig-design-make-${n}`}>
                <img src={`${BACKEND}/api/admin/instagram/discount-preview/${n}.jpg`} alt={`Diseño ${n}`} loading="lazy" className="w-full aspect-[4/5] object-cover bg-violet-100 transition-transform group-hover:scale-[1.02]" />
                <span className="absolute bottom-10 right-2 inline-flex items-center gap-1 rounded-full bg-amber-400 px-2 py-1 text-[11px] font-bold text-violet-900 shadow"><Download className="w-3 h-3" />Crear</span>
              </button>
              <button type="button" onClick={() => toggle(n)} aria-pressed={on} aria-label={on ? "Desactivar diseño" : "Activar diseño"}
                className={`absolute top-2 left-2 grid place-items-center w-8 h-8 rounded-full text-xs font-bold shadow ${on ? "bg-primary text-white" : "bg-white/95 text-violet-800"}`} data-testid={`ig-design-toggle-${n}`}>{on ? <Check className="w-4 h-4" /> : n}</button>
              <p className="px-2 py-1.5 text-xs font-semibold bg-background">Diseño {n}</p>
            </div>
          );
        })}
      </div>
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs text-muted-foreground" data-testid="ig-design-count">{sel.length} de 6 activos</span>
        <Button size="sm" onClick={save} disabled={busy || sel.length === 0} data-testid="ig-design-save">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Guardar diseños"}</Button>
      </div>
      <Dialog open={!!making} onOpenChange={(v) => { if (!v) setMaking(null); }}>
        <DialogContent className="max-w-md" data-testid="ig-make-dialog">
          <DialogHeader><DialogTitle>Diseño para Instagram {making?.n}</DialogTitle><DialogDescription>Crea la imagen o el reel con los datos del descuento y descárgalo.</DialogDescription></DialogHeader>
          {making && <MakeDownload n={making.n} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const plus30 = () => new Date(Date.now() + 30 * 864e5).toLocaleDateString("en-CA", { timeZone: "America/Santiago" });

async function downloadBlob(promise, filename) {
  const { data } = await promise;
  const url = URL.createObjectURL(data);
  const a = document.createElement("a");
  a.href = url; a.download = filename; document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}

function MakeDownload({ n }) {
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(null);
  useEffect(() => {
    api.get("/admin/discount-campaigns").then(({ data }) => {
      const t = new Date().toLocaleDateString("en-CA", { timeZone: "America/Santiago" });
      const c = data.filter((x) => x.end_date >= t).sort((a, b) => a.start_date.localeCompare(b.start_date))[0];
      setF(c ? { pct: String(c.pct), until: c.end_date, from: c.name } : { pct: "40", until: plus30(), from: null });
    }).catch(() => setF({ pct: "40", until: plus30(), from: null }));
  }, []);
  if (!f) return <Loader2 className="w-5 h-5 animate-spin text-primary" />;
  const make = async (kind) => {
    setBusy(kind);
    try {
      await downloadBlob(api.post(`/admin/instagram/discount-render/${n}`, { pct: Number(f.pct), until: f.until, kind }, { responseType: "blob" }),
        `vivotaller-descuento-${f.pct}-diseno${n}.${kind === "image" ? "jpg" : "mp4"}`);
      toast.success(kind === "image" ? "Imagen descargada" : "Reel descargado");
    } catch { toast.error("No pudimos crear el archivo. Inténtalo nuevamente."); }
    finally { setBusy(null); }
  };
  return (
    <div className="space-y-4" data-testid="ig-make-form">
      {f.from && <p className="text-xs rounded-lg bg-violet-50 text-violet-800 px-3 py-2">Datos tomados del período «{f.from}». Puedes cambiarlos.</p>}
      <div className="grid grid-cols-2 gap-3">
        <div><Label>Descuento</Label>
          <Select value={f.pct} onValueChange={(v) => setF({ ...f, pct: v })}>
            <SelectTrigger data-testid="ig-make-pct"><SelectValue /></SelectTrigger>
            <SelectContent className="max-h-60">{Array.from({ length: 20 }, (_, i) => (i + 1) * 5).map((v) => <SelectItem key={v} value={String(v)}>{v}%</SelectItem>)}</SelectContent>
          </Select></div>
        <div><Label>Válido hasta</Label><DateField value={f.until} onChange={(v) => setF({ ...f, until: v })} testId="ig-make-until" floating /></div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <Button variant="outline" disabled={!!busy} onClick={() => make("image")} data-testid="ig-make-image">{busy === "image" ? <Loader2 className="w-4 h-4 animate-spin" /> : <><ImageDown className="w-4 h-4 mr-1" />Descargar imagen</>}</Button>
        <Button disabled={!!busy} onClick={() => make("reel")} data-testid="ig-make-reel">{busy === "reel" ? <><Loader2 className="w-4 h-4 animate-spin mr-1" />Creando reel…</> : <><Film className="w-4 h-4 mr-1" />Descargar reel</>}</Button>
      </div>
      <p className="text-[11px] text-muted-foreground">Imagen 1080×1350 para publicación · Reel vertical de 7 s. Las fotos de la comunidad cambian cada vez que creas uno.</p>
    </div>
  );
}

function PostDialog({ post, configured, onClose, onChanged }) {
  const [caption, setCaption] = useState(post.caption);
  const [busy, setBusy] = useState(false);
  const run = async (fn, ok) => {
    setBusy(true);
    try { await fn(); toast.success(ok); onChanged(); onClose(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const save = () => api.put(`/admin/instagram/discount-posts/${post.id}`, { caption });
  const approve = async () => {
    await save();
    const { data } = await api.post(`/admin/instagram/discount-posts/${post.id}/approve`);
    if (data.status === "failed") throw { response: { data: { detail: `Instagram respondió: ${data.error}` } } };
  };
  return (
    <div className="space-y-3" data-testid="ig-discount-editor">
      <video src={local(post.video)} poster={local(post.media)} controls autoPlay muted loop playsInline className="w-full max-w-[240px] mx-auto rounded-xl border bg-violet-950 aspect-[9/16]" data-testid="ig-discount-video" />
      <p className="text-xs text-center text-muted-foreground">Reel de 7 segundos (formato vertical). Si es la primera vez, puede tardar unos segundos en cargar.</p>
      <div className="grid grid-cols-2 gap-2">
        <Button variant="outline" size="sm" onClick={() => downloadBlob(fetch(local(post.media)).then(async (r) => ({ data: await r.blob() })), `vivotaller-descuento-${post.pct}.jpg`)} data-testid="ig-discount-dl-image"><ImageDown className="w-4 h-4 mr-1" />Bajar imagen</Button>
        <Button variant="outline" size="sm" onClick={() => downloadBlob(fetch(local(post.video)).then(async (r) => ({ data: await r.blob() })), `vivotaller-descuento-${post.pct}.mp4`)} data-testid="ig-discount-dl-reel"><Film className="w-4 h-4 mr-1" />Bajar reel</Button>
      </div>
      <Textarea rows={9} value={caption} onChange={(e) => setCaption(e.target.value)} maxLength={2200} className="text-sm" data-testid="ig-discount-caption" />
      {!configured && <p className="text-xs rounded-lg bg-amber-50 border border-amber-300 text-amber-800 p-2">La cuenta de Instagram aún no está conectada. Al aprobar, la publicación queda en cola.</p>}
      <div className="flex flex-wrap justify-end gap-2">
        <Button variant="ghost" size="sm" className="text-destructive" disabled={busy} onClick={() => run(() => api.delete(`/admin/instagram/discount-posts/${post.id}`), "Publicación quitada")} data-testid="ig-discount-remove"><Trash2 className="w-4 h-4 mr-1" />Quitar</Button>
        <Button variant="outline" size="sm" disabled={busy} onClick={() => run(save, "Borrador guardado")} data-testid="ig-discount-save"><Save className="w-4 h-4 mr-1" />Guardar</Button>
        <Button size="sm" disabled={busy} onClick={() => run(approve, configured ? "Publicado en Instagram" : "Aprobado: quedó en cola")} data-testid="ig-discount-approve">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4 mr-1" />{configured ? "Aprobar y publicar reel" : "Aprobar"}</>}
        </Button>
      </div>
    </div>
  );
}

export function DiscountInstagram({ configured, StatusChip }) {
  const [posts, setPosts] = useState(null);
  const [open, setOpen] = useState(null);
  const load = () => api.get("/admin/instagram/discount-posts").then((r) => setPosts(r.data));
  useEffect(() => { load(); }, []);
  return (
    <div className="pt-4 border-t space-y-4" data-testid="ig-discount-section">
      <p className="font-semibold flex items-center gap-1.5"><Percent className="w-4 h-4 text-primary" />Publicaciones de descuentos</p>
      <DesignPicker />
      {posts && posts.length > 0 && (
        <div className="space-y-2" data-testid="ig-discount-posts">
          {posts.map((p) => (
            <div key={p.id} className="flex items-center gap-3 rounded-xl border p-3" data-testid={`ig-discount-row-${p.id}`}>
              <img src={local(p.media)} alt="" className="w-14 h-[70px] rounded-md object-cover border shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="font-semibold truncate">{p.campaign_name} · {p.pct}%</p>
                <p className="text-xs text-muted-foreground">Reel · Diseño para Instagram {p.design}</p>
                <div className="mt-1"><StatusChip s={p.status} id={`d-${p.id}`} /></div>
              </div>
              {p.status !== "published" && <Button size="sm" variant="outline" onClick={() => setOpen(p)} data-testid={`ig-discount-open-${p.id}`}>Revisar</Button>}
            </div>
          ))}
        </div>
      )}
      <Dialog open={!!open} onOpenChange={(v) => { if (!v) setOpen(null); }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Descuento en Instagram</DialogTitle><DialogDescription>{open?.campaign_name}</DialogDescription></DialogHeader>
          {open && <PostDialog post={open} configured={configured} onClose={() => setOpen(null)} onChanged={load} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
